import { AgentComparison, AgentStatistics } from '@rma/public/compare-agent/private-api/da-compare-agent';

export type SalesPerformanceDetails = Pick<AgentComparison, 'agentCode' | 'sales' | 'statistics' | 'agentProfilePropertiesUrl'>;

export type SalesPerformanceRowKey =
  | 'totalSales'
  | 'salesAsLeadAgent'
  | 'salesAsSupportingAgent'
  | 'salesAsBuyersAgent'
  | 'currentListings'
  | 'averageSalesPrice'
  | 'totalSalesValue'
  | 'averageDaysOnMarket';

export type SalesPerformanceFormat = 'count' | 'currency' | 'days';

export interface SalesPerformanceRow {
  key: SalesPerformanceRowKey;
  labelKey: string;
  value: (agent: SalesPerformanceDetails, propertyType: string | null) => number | undefined;
  format: SalesPerformanceFormat;
  indented?: boolean;
  zeroAsEmpty?: boolean;
  link?: (agent: SalesPerformanceDetails) => string | undefined;
}

export const hasSalesPerformanceValue = (value: number | null | undefined): value is number => value !== undefined && value !== null;

export const showsSalesPerformanceValue = (row: SalesPerformanceRow, value: number | null | undefined): value is number =>
  hasSalesPerformanceValue(value) && !(row.zeroAsEmpty && value === 0);

const statisticValue = (statistic: AgentStatistics | undefined, propertyType: string | null): number | undefined =>
  propertyType === null ? statistic?.totalValue : statistic?.byPropertyType?.[propertyType];

export const SALES_PERFORMANCE_ROWS: readonly SalesPerformanceRow[] = [
  {
    key: 'totalSales',
    labelKey: 'totalSales',
    value: (agent, propertyType) =>
      propertyType === null ? agent.sales?.total : statisticValue(agent.statistics?.totaL_SALES_QTY, propertyType),
    format: 'count',
  },
  {
    key: 'salesAsLeadAgent',
    labelKey: 'salesAsLeadAgent',
    value: (agent, propertyType) => (propertyType === null ? agent.sales?.lead : undefined),
    format: 'count',
    indented: true,
    zeroAsEmpty: true,
  },
  {
    key: 'salesAsSupportingAgent',
    labelKey: 'salesAsSupportingAgent',
    value: (agent, propertyType) => (propertyType === null ? agent.sales?.support : undefined),
    format: 'count',
    indented: true,
    zeroAsEmpty: true,
  },
  {
    key: 'salesAsBuyersAgent',
    labelKey: 'salesAsBuyersAgent',
    value: (agent, propertyType) => (propertyType === null ? agent.statistics?.totaL_SALES_QTY?.buyerAgentValue : undefined),
    format: 'count',
    indented: true,
    zeroAsEmpty: true,
  },
  {
    key: 'currentListings',
    labelKey: 'currentListings',
    value: (agent, propertyType) => statisticValue(agent.statistics?.activE_CAMPAIGNS, propertyType),
    format: 'count',
    link: (agent) => agent.agentProfilePropertiesUrl,
  },
  {
    key: 'averageSalesPrice',
    labelKey: 'averageSalesPrice',
    value: (agent, propertyType) => statisticValue(agent.statistics?.avG_SALE_PRICE, propertyType),
    format: 'currency',
  },
  {
    key: 'totalSalesValue',
    labelKey: 'totalSalesValue',
    value: (agent, propertyType) => statisticValue(agent.statistics?.totaL_SALES_VALUE, propertyType),
    format: 'currency',
  },
  {
    key: 'averageDaysOnMarket',
    labelKey: 'averageDaysOnMarket',
    value: (agent, propertyType) => statisticValue(agent.statistics?.avG_DAYS_ON_MARKET, propertyType),
    format: 'days',
  },
];

export const visibleSalesPerformanceRows = (
  agents: readonly SalesPerformanceDetails[],
  buyersAgentEnabled: boolean,
  propertyType: string | null,
): SalesPerformanceRow[] =>
  SALES_PERFORMANCE_ROWS.filter((row) => {
    if (row.key === 'salesAsBuyersAgent' && !buyersAgentEnabled) {
      return false;
    }

    return !row.indented || agents.some((agent) => showsSalesPerformanceValue(row, row.value(agent, propertyType)));
  });
